/**
 * Modal when Fokus is clicked with no selection — pick scope (available / in progress / both).
 */

import React, { useMemo, useState, useEffect } from 'react';
import ConfirmModal from '../../../FlowchartView/components/ConfirmModal';
import './FokusEmptySelectionModal.css';

const CHOICE_AVAILABLE = 'available';
const CHOICE_IN_PROGRESS = 'inProgress';
const CHOICE_BOTH = 'both';

const OptionRow = ({ name, checked, onChange, title, badge }) => (
  <label className="fokus-empty-selection-modal__option">
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={onChange}
      className="fokus-empty-selection-modal__input"
    />
    <span className="fokus-empty-selection-modal__surface">
      <span className="fokus-empty-selection-modal__radio" aria-hidden="true">
        <span className="fokus-empty-selection-modal__radio-dot" />
      </span>
      <span className="fokus-empty-selection-modal__main">
        <span className="fokus-empty-selection-modal__label">{title}</span>
        <span className="fokus-empty-selection-modal__badge">{badge}</span>
      </span>
    </span>
  </label>
);

const FokusEmptySelectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  availableTaskIds = [],
  inProgressTaskIds = [],
}) => {
  const [choice, setChoice] = useState(CHOICE_BOTH);

  useEffect(() => {
    if (isOpen) setChoice(CHOICE_BOTH);
  }, [isOpen]);

  const bothCount = useMemo(
    () => new Set([...availableTaskIds, ...inProgressTaskIds]).size,
    [availableTaskIds, inProgressTaskIds],
  );

  const resolvedIds = useMemo(() => {
    const avail = new Set(availableTaskIds);
    const prog = new Set(inProgressTaskIds);
    if (choice === CHOICE_AVAILABLE) return Array.from(avail);
    if (choice === CHOICE_IN_PROGRESS) return Array.from(prog);
    return Array.from(new Set([...availableTaskIds, ...inProgressTaskIds]));
  }, [choice, availableTaskIds, inProgressTaskIds]);

  const confirmDisabled = resolvedIds.length === 0;

  const handleConfirm = () => {
    if (confirmDisabled) return;
    onConfirm(new Set(resolvedIds));
    onClose();
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      title="Nothing selected"
      message="You haven't selected anything. What would you like to focus on?"
      confirmLabel="Fokus"
      cancelLabel="Cancel"
      confirmVariant="primary"
      icon="🧠"
      confirmDisabled={confirmDisabled}
      onConfirm={handleConfirm}
      onCancel={onClose}
    >
      <div className="fokus-empty-selection-modal" role="radiogroup" aria-label="Fokus scope">
        <OptionRow
          name="fokus-empty-scope"
          checked={choice === CHOICE_AVAILABLE}
          onChange={() => setChoice(CHOICE_AVAILABLE)}
          title="All available missions"
          badge={availableTaskIds.length}
        />
        <OptionRow
          name="fokus-empty-scope"
          checked={choice === CHOICE_IN_PROGRESS}
          onChange={() => setChoice(CHOICE_IN_PROGRESS)}
          title="All in-progress missions"
          badge={inProgressTaskIds.length}
        />
        <OptionRow
          name="fokus-empty-scope"
          checked={choice === CHOICE_BOTH}
          onChange={() => setChoice(CHOICE_BOTH)}
          title="Both (available and in-progress)"
          badge={bothCount}
        />
        {confirmDisabled && (
          <p className="fokus-empty-selection-modal__hint">No missions match this choice right now.</p>
        )}
      </div>
    </ConfirmModal>
  );
};

export default FokusEmptySelectionModal;
